// BatchesPage — バッチ完全性台帳 (#batches, blueprint v2 Phase 0, 2026-07-04)
//
// 一括承認N件の「全件間違いなく処理されたか」を台帳で証明する画面。
//   - 「登録 n/N・照合 n/N」2段カウンタ + 詳細は「登録・照合・証憑添付」大型進捗ヘッダ。
//     緑 = freee実データと機械照合済み(verified)の事実
//   - 1件でも失敗/不一致があればバッチは赤く残留 (dismiss不存在。全件verified か 理由必須closeのみ)
//   - 照合はfreee単体GETとの突合(worker決定論・10分毎+承認直後)。「たぶん全部できた」の廃止
// デザインは JournalReviewPage のパレットC準拠 (Ledger UI Kit 先行流用)。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import { MdRefresh, MdLockOutline, MdWarningAmber, MdCheckCircle } from 'react-icons/md';
import {
  fetchBatches, fetchBatchDetail, retryBatchFailed, closeBatch,
  BatchSummary, BatchItem, BatchCounts,
} from '../api/client';
import { batchStatusToUnified, batchItemToUnified } from '../lib/statusVocab';

const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386', line: '#d9e0e7', lineDark: '#bdc8d3',
  navy: '#203a57', good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
const NUM = '"SFMono-Regular",Consolas,Menlo,monospace';
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",sans-serif';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, overflow: 'hidden' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '8px 18px 8px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid rgba(255,255,255,.35)', borderRadius: 13, background: 'rgba(255,255,255,.12)', color: '#e5eef8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  list: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  // attention枠 = 橙 (あなたの番)。赤は中の「失敗/不一致」事実チップのみ (統一色規則)
  card: (attention: boolean): React.CSSProperties => ({ marginBottom: 12, border: `1px solid ${attention ? '#e3c68a' : C.lineDark}`, borderRadius: 7, background: '#fff', overflow: 'hidden', cursor: 'pointer' }),
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 15px', flexWrap: 'wrap' } as React.CSSProperties,
  counter: (color: string): React.CSSProperties => ({ fontFamily: NUM, fontSize: 16, fontWeight: 900, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }),
  badge: (bg: string, fg: string, bd: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 800, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }),
  th: { textAlign: 'left', padding: '7px 10px', borderBottom: `1px solid ${C.lineDark}`, fontSize: 10, color: C.sub, fontWeight: 800, letterSpacing: '.05em', background: '#f4f7fa', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '7px 10px', borderBottom: `1px solid ${C.line}`, fontSize: 12, verticalAlign: 'top' } as React.CSSProperties,
};

const parseCounts = (b: BatchSummary): BatchCounts => {
  try { return { pending: 0, registering: 0, posted: 0, verified: 0, verify_failed: 0, failed: 0, unverifiable: 0, ...(JSON.parse(b.counts_json || '{}')) }; }
  catch { return { pending: 0, registering: 0, posted: 0, verified: 0, verify_failed: 0, failed: 0, unverifiable: 0 }; }
};
// counts_json の receipts_attached (証憑添付済み件数)。worker が追加予定 — 未提供の間は null (UIは 0 表示で耐える)
const receiptsAttachedOf = (b: BatchSummary): number | null => {
  try {
    const j = JSON.parse(b.counts_json || '{}');
    return typeof j.receipts_attached === 'number' ? j.receipts_attached : null;
  } catch { return null; }
};
// 「システムが作業中 (=自動更新を回すべき)」判定。失敗/不一致の残はユーザーの番なので処理中扱いしない
const isBatchProcessing = (b: BatchSummary): boolean => {
  if (b.status === 'closed') return false;
  const c = parseCounts(b);
  if (c.pending + c.registering + c.posted > 0) return true;
  const ra = receiptsAttachedOf(b);
  return ra !== null && ra < b.total_count && c.failed === 0 && c.verify_failed === 0;
};
const fmtTs = (t: number | null | undefined) => {
  if (!t) return '—';
  const d = new Date(t); const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// item行の「状態」列 = 行動レイヤ (統一語彙 lib/statusVocab.ts)。posted は機械照合を待つだけ=青。
// verify_failed/failed の「照合不一致/失敗」は赤の事実チップとして行動バッジの隣に別掲する。
const STATE_LABEL: Record<string, [string, string, string, string]> = Object.fromEntries(
  ['pending', 'registering', 'posted', 'verified', 'verify_failed', 'failed'].map((s) => {
    const u = batchItemToUnified(s);
    return [s, [u.label, u.bg, u.fg, u.bd]];
  }),
) as Record<string, [string, string, string, string]>;
// 事実レイヤ (赤チップ): 何が起きたか
const STATE_FACT: Record<string, string> = {
  verify_failed: '照合不一致',
  failed: '失敗',
};

const readHashId = (): string | null => {
  const m = window.location.hash.match(/^#?batches\/([^/]+)/);
  return m ? m[1] : null;
};

export const BatchesPage: React.FC = () => {
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [detailId, setDetailId] = useState<string | null>(() => readHashId());
  const [detail, setDetail] = useState<{ batch: BatchSummary; items: BatchItem[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // silent=true は自動更新用 — loading スピナーを出さずに差し替え (5秒毎のチラつき防止)
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetchBatches();
      setBatches(r.batches);
      const id = readHashId();
      setDetailId(id);
      if (id) setDetail(await fetchBatchDetail(id));
      else setDetail(null);
    } catch (e: unknown) { setMsg(`取得失敗: ${e instanceof Error ? e.message : String(e)}`); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onHash = () => { setDetailId(readHashId()); load(); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [load]);
  // 処理中(open)バッチがある間は自動更新 — 「承認したが今どうなっているか不明」の解消。
  // 詳細を開いていて処理中なら5秒、それ以外は10秒 (silent更新)
  useEffect(() => {
    const detailProcessing = !!(detailId && detail && detail.batch.id === detailId && isBatchProcessing(detail.batch));
    const hasActive = batches.some(isBatchProcessing);
    if (!hasActive && !detailId) return;
    const t = setInterval(() => load(true), detailProcessing ? 5000 : 10000);
    return () => clearInterval(t);
  }, [batches, detail, detailId, load]);

  const retryAll = useCallback(async (id: string) => {
    await retryBatchFailed(id); await load();
  }, [load]);
  const doClose = useCallback(async (id: string, allVerified: boolean) => {
    const reason = allVerified ? '' : (window.prompt('全件照合済みではありません。closeする理由を入力してください(監査記録に残ります):') || '');
    if (!allVerified && !reason.trim()) return;
    await closeBatch(id, reason); await load();
  }, [load]);

  const CounterRow: React.FC<{ b: BatchSummary }> = ({ b }) => {
    const c = parseCounts(b);
    const registered = c.posted + c.verified + c.verify_failed;
    const regDone = registered >= b.total_count && c.failed === 0;
    const allGreen = c.verified === b.total_count && c.unverifiable === 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={S.counter(regDone ? C.good : c.failed > 0 ? C.danger : C.ink)}>登録 {registered + c.failed > b.total_count ? b.total_count : registered}/{b.total_count}</span>
        <span style={S.counter(allGreen ? C.good : c.verify_failed > 0 ? C.danger : C.warn)}>照合 {c.verified}/{b.total_count}</span>
        {allGreen && b.total_count > 0 && <span style={S.badge(C.goodBg, C.good, '#9fdbc7')}><MdCheckCircle size={12} /> 全件照合済み(freee確認済)</span>}
        {(c.failed > 0 || c.verify_failed > 0) && <span style={S.badge(C.dangerBg, C.danger, '#e6b0ac')}><MdWarningAmber size={12} /> 失敗{c.failed}・不一致{c.verify_failed}</span>}
        {c.unverifiable > 0 && <span style={S.badge(C.warnBg, C.warn, '#e3c68a')}>検証不能 {c.unverifiable}</span>}
      </div>
    );
  };

  // 詳細を開いた時の大型進捗ヘッダ: 「登録 n/N・照合 n/N・証憑添付 n/N」+ 進捗バー + 現在活動行。
  // receipts_attached は worker が counts_json に追加予定 — 無い間は 0/N + (集計準備中) 表示で耐える。
  const DetailProgress: React.FC<{ b: BatchSummary }> = ({ b }) => {
    const c = parseCounts(b);
    const N = b.total_count;
    const registered = Math.min(c.posted + c.verified + c.verify_failed, N);
    const attachedRaw = receiptsAttachedOf(b);
    const attached = Math.min(attachedRaw ?? 0, N);
    const complete = N > 0 && c.verified === N && c.unverifiable === 0 && attachedRaw !== null && attached >= N;
    const processing = isBatchProcessing(b);
    // 統一語彙: 青=処理中(操作不要)/橙=対応待ち/緑=完了。赤は失敗・不一致の事実表示のみ
    const BLUE = { bg: '#f0f6ff', fg: '#33699e', bd: '#b9d2ed' };
    const activity: { text: string; bg: string; fg: string; bd: string; spin?: boolean } | null = (() => {
      if (complete) return null;
      if (b.status === 'closed') return { text: 'close済み — このバッチの追跡は終了しています', bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' };
      if (c.failed > 0 || c.verify_failed > 0) return { text: `対応待ち(要修正) — 失敗${c.failed}件・照合不一致${c.verify_failed}件。「失敗分を再試行」または理由を付けてcloseしてください`, bg: C.dangerBg, fg: C.danger, bd: '#e6b0ac' };
      if (c.pending + c.registering > 0) return { text: `現在: 登録処理中 — 残り${c.pending + c.registering}件 (workerが承認直後+10分毎に自動処理)`, ...BLUE, spin: true };
      if (c.posted > 0) return { text: `現在: freee照合中 — 残り${c.posted}件 (freee実データと機械突合)`, ...BLUE, spin: true };
      if (attachedRaw !== null && attached < N) return { text: `現在: 証憑添付 処理中 — 残り${N - attached}件 (証憑をfreee dealへ添付)`, ...BLUE, spin: true };
      if (attachedRaw === null && c.verified === N && c.unverifiable === 0) return { text: '全件照合済み — 証憑添付の件数集計は準備中のため 0 表示です', bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' };
      if (c.unverifiable > 0) return { text: `検証不能 ${c.unverifiable}件 — 内容の確認が必要です`, bg: C.warnBg, fg: C.warn, bd: '#e3c68a' };
      return null;
    })();
    const bar = (value: number, color: string) => (
      <div style={{ height: 7, borderRadius: 4, background: '#e3e9ef', overflow: 'hidden', marginTop: 6 }}>
        <div style={{ width: `${N > 0 ? Math.min(100, (value / N) * 100) : 0}%`, height: '100%', background: color, transition: 'width .4s ease' }} />
      </div>
    );
    const cell = (label: string, value: number, color: string, note?: string) => (
      <div style={{ minWidth: 150, flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: C.sub, letterSpacing: '.06em' }}>
          {label}{note ? <span style={{ fontWeight: 600 }}> {note}</span> : null}
        </div>
        <div style={{ fontFamily: NUM, fontSize: 24, fontWeight: 900, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
          {value}<span style={{ fontSize: 14, color: C.sub, fontWeight: 800 }}> / {N}</span>
        </div>
        {bar(value, color)}
      </div>
    );
    return (
      <div style={{ border: `1px solid ${C.lineDark}`, borderRadius: 7, background: '#fff', padding: '14px 16px', marginBottom: 10 }}>
        {complete && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: C.goodBg, border: '1px solid #9fdbc7', color: C.good, fontWeight: 800, fontSize: 13, marginBottom: 12 }}>
            <MdCheckCircle size={18} /> 全件完了 — 登録・照合・証憑添付 すべて {N}/{N} (freee実データで確認済み)
          </div>
        )}
        {activity && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: activity.bg, border: `1px solid ${activity.bd}`, color: activity.fg, fontWeight: 700, fontSize: 13, marginBottom: 12 }}>
            {activity.spin ? <InlineSpinner isLoading /> : null}
            <span>{activity.text}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {cell('登録', registered, registered >= N && c.failed === 0 ? C.good : c.failed > 0 ? C.danger : BLUE.fg)}
          {cell('照合', Math.min(c.verified, N), c.verified >= N && N > 0 ? C.good : c.verify_failed > 0 ? C.danger : BLUE.fg)}
          {cell('証憑添付', attached, attachedRaw !== null && attached >= N && N > 0 ? C.good : BLUE.fg, attachedRaw === null ? '(集計準備中)' : undefined)}
        </div>
        {processing && (
          <div style={{ marginTop: 10, fontSize: 11, color: C.sub }}>処理中は5秒ごとに自動更新します — この画面を開いたまま進捗をご確認いただけます</div>
        )}
      </div>
    );
  };

  return (
    <div style={S.page}>
      <Container>
        <section style={S.sheet}>
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <h1 style={S.topTitle}>バッチ完全性台帳</h1>
              <span style={S.topCaption}>一括承認の全件が freee 上で確認されるまで追跡する台帳 — 処理中(照合待ち)は操作不要、対応待ち(要修正)のみ再試行/close を</span>
              <span style={S.pill}><MdLockOutline size={13} /> 照合 = freee 実データとの機械突合</span>
            </div>
            <Button appearance="secondary" small onClick={() => load()} disabled={loading}>
              {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 更新
            </Button>
          </header>

          {msg && <div style={{ padding: 12 }}><MessageBlock error>{msg}</MessageBlock></div>}

          <section style={S.list}>
            {batches.length === 0 && !loading && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontWeight: 700 }}>
                バッチはまだありません。請求書処理ページで「一括承認」すると、ここに全件の登録・照合状況が現れます。
              </div>
            )}
            {batches.map((b) => {
              const isOpen = detailId === b.id;
              const c = parseCounts(b);
              const allGreen = c.verified === b.total_count && c.unverifiable === 0;
              return (
                <div key={b.id} style={S.card(b.status === 'attention')}>
                  <div style={S.cardHead} onClick={() => { window.location.hash = isOpen ? '#batches' : `#batches/${b.id}`; }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <b style={{ fontFamily: NUM, fontSize: 13 }}>{b.id}</b>
                        <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>{b.kind}</span>
                        {b.status === 'attention' && (() => { const u = batchStatusToUnified('attention'); return <span title="batches.status=attention — 失敗分を再試行するか、理由を付けてcloseしてください" style={S.badge(u.bg, u.fg, u.bd)}>{u.label}</span>; })()}
                        {b.status === 'closed' && <span style={S.badge('#f1f4f7', '#5e7083', '#ccd6df')}>close済{b.close_reason && b.close_reason !== 'all_verified' ? `(${b.close_reason})` : ''}</span>}
                      </div>
                      <span style={{ color: C.sub, fontSize: 11 }}>{fmtTs(b.created_at)} ／ {b.total_count}件{b.total_amount ? ` ¥${Number(b.total_amount).toLocaleString('ja-JP')}` : ''} ／ {b.created_by}</span>
                    </div>
                    <CounterRow b={b} />
                  </div>

                  {isOpen && detail && detail.batch.id === b.id && (
                    <div style={{ borderTop: `1px solid ${C.lineDark}`, padding: '10px 15px 14px', background: '#fbfcfd' }}>
                      <DetailProgress b={detail.batch} />
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        {(c.failed > 0 || c.verify_failed > 0) && (
                          <Button appearance="primary" small onClick={(e: any) => { e.stopPropagation(); retryAll(b.id); }}>失敗分を再試行</Button>
                        )}
                        {b.status !== 'closed' && (
                          <Button appearance="secondary" small onClick={(e: any) => { e.stopPropagation(); doClose(b.id, allGreen); }}>
                            {allGreen ? 'closeする(全件照合済)' : '理由を付けてclose'}
                          </Button>
                        )}
                      </div>
                      <div style={{ overflowX: 'auto', border: `1px solid ${C.lineDark}`, borderRadius: 5 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                          <thead><tr>
                            <th style={S.th}>#</th><th style={S.th}>REF</th><th style={S.th}>状態</th>
                            <th style={S.th}>freee</th><th style={S.th}>登録</th><th style={S.th}>照合</th><th style={S.th}>詳細</th>
                          </tr></thead>
                          <tbody>
                            {detail.items.map((it) => {
                              const [label, bg, fg, bd] = STATE_LABEL[it.state] || [it.state, '#eee', '#333', '#ccc'];
                              let errText = '';
                              try { const e = JSON.parse(it.error_json || 'null'); if (e) errText = `${e.message_user || e.code}${e.next_action ? ` → ${e.next_action}` : ''}`; } catch {}
                              let evText = '';
                              try { const ev = JSON.parse(it.verify_evidence_json || 'null'); if (ev && ev.diffs && ev.diffs.length) evText = ev.diffs.map((d: any) => `${d.field}: 期待${d.expected}≠実${d.actual}`).join(' / '); } catch {}
                              return (
                                <tr key={it.id}>
                                  <td style={{ ...S.td, fontFamily: NUM }}>{String(it.seq).padStart(3, '0')}</td>
                                  <td style={{ ...S.td, fontFamily: NUM }}>{it.target_ref}</td>
                                  <td style={S.td}>
                                    <span style={S.badge(bg, fg, bd)} title={it.state}>{label}</span>
                                    {STATE_FACT[it.state] ? <span style={{ ...S.badge(C.dangerBg, C.danger, '#e6b0ac'), marginLeft: 4 }}>{STATE_FACT[it.state]}</span> : null}
                                    {it.recovered_at ? <span style={{ ...S.badge('#f0f6ff', '#33699e', '#b9d2ed'), marginLeft: 4 }}>marker回復</span> : null}
                                  </td>
                                  <td style={S.td}>{it.freee_deal_id
                                    ? <a href={`https://secure.freee.co.jp/deals#deal_id=${it.freee_deal_id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#476b8e', fontFamily: NUM, fontSize: 11 }}>{it.freee_deal_id} ↗</a>
                                    : '—'}</td>
                                  <td style={{ ...S.td, fontFamily: NUM, fontSize: 11 }}>{fmtTs(it.posted_at)}</td>
                                  <td style={{ ...S.td, fontFamily: NUM, fontSize: 11 }}>{fmtTs(it.verified_at)}</td>
                                  <td style={{ ...S.td, color: errText || evText ? C.danger : C.sub, fontSize: 11, maxWidth: 340 }}>{errText || evText || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </section>
      </Container>
    </div>
  );
};

export default BatchesPage;
